import { duration, utc } from '../format.ts';
import type { Tone } from '../state.ts';
import type { Command, CommandStatus } from '../../hooks/useCommands.ts';

/* ==========================================================================
   EVENTS ON THE TIME AXIS.

   THE BAR §5 is explicit: "Events are marks at their true timestamps, not a
   list beside the plot: coloured diamonds for instants, bars for durations,
   the edited one outlined" (`foxglove-event-editor-timeline-markers.png`),
   and §13 tell 21 requires the scrub bar to carry them too.

   This console's events are its COMMANDS. A command is the only thing an
   operator does to a robot, and "did it fault because of what we sent it" is
   the question the whole surface exists to answer — which is unanswerable
   while the dispatch is a row in a table on another route and the fault is a
   step in a state ribbon here. Put the dispatch on the same axis as the
   telemetry and the answer is a vertical read.

   A command is BOTH an instant and a duration: `issued_at` is when it left,
   `updated_at` is when it reached the state it is in now. So it draws as a
   diamond at the instant with a bar running to its resolution — the exact
   pair the reference frame uses.

   Nothing here fetches. The workspace hands the kit rows from
   `/api/robots/{id}/commands` and gets marks back, so robot detail and teleop
   can put the same marks on their own axes without either of them owning the
   mapping from a command's status to a colour.
   ========================================================================== */

export interface EventMark {
  key: string;
  /** When it happened — `issued_at`. Epoch ms. */
  at: number;
  /** When it reached its current state. `null` for an instant with no span. */
  end: number | null;
  /** The machine's own name for it, set in mono wherever it is printed. */
  label: string;
  /** Terminal or in-flight status, as the contract words it. */
  status: string;
  tone: Tone;
  /** Everything the mark cannot draw, for the title on hover. */
  detail: string;
}

const CMD_TONE: Record<CommandStatus, Tone> = {
  pending: 'stale',
  sent: 'charging',
  acked: 'idle',
  completed: 'good',
  failed: 'error',
  cancelled: 'neutral',
};

/** True while the command has not reached a terminal state. */
export function commandOpen(status: CommandStatus): boolean {
  return status === 'pending' || status === 'sent' || status === 'acked';
}

/**
 * Commands → marks.
 *
 * `now` closes the bar of a command that has not finished: an in-flight
 * dispatch's span is "since it was issued", and drawing it as an instant
 * would make a stop that has been unacknowledged for sixteen hours look
 * exactly like one that landed immediately.
 */
export function commandMarks(commands: readonly Command[], now: number): EventMark[] {
  const out: EventMark[] = [];
  for (const c of commands) {
    const at = new Date(c.issued_at).getTime();
    if (!Number.isFinite(at)) continue;
    const open = commandOpen(c.status);
    const closed = new Date(c.updated_at).getTime();
    const end = open ? now : Number.isFinite(closed) && closed > at ? closed : null;
    const span = end === null ? null : end - at;
    const err = c.result?.['error'];
    out.push({
      key: `cmd-${c.id}`,
      at,
      end,
      label: c.command,
      status: c.status,
      tone: CMD_TONE[c.status] ?? 'neutral',
      detail:
        `${c.command} — ${c.status}\n`
        + `issued ${utc(c.issued_at)} by ${c.issued_by}\n`
        + (span === null
          ? ''
          : `${open ? 'unresolved for' : 'resolved after'} ${duration(span)}\n`)
        + (typeof err === 'string' ? `${err}\n` : ''),
    });
  }
  return out.sort((a, b) => a.at - b.at);
}

/**
 * The command that owns an instant: the NEWEST one still running at `t`, or
 * failing that the newest one issued before it. Returns null when `t` is
 * earlier than anything on the list — a window that opens before the first
 * dispatch has no "last command", and inventing one would be a claim about a
 * robot nobody had touched yet.
 *
 * Newest, not first-match: an `acked` mapping run that was never closed stays
 * open for the rest of the record, so a first-match scan would go on
 * reporting it hours after three later commands had come and gone.
 */
export function markAt(marks: readonly EventMark[], t: number): EventMark | null {
  let open: EventMark | null = null;
  let last: EventMark | null = null;
  for (const m of marks) {
    if (m.at > t) break;
    if (m.end !== null && t <= m.end) open = m;
    last = m;
  }
  return open ?? last;
}
